import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import DashboardLayout from '../components/DashboardLayout';
import { getNotificationsSocket } from '../lib/notificationsSocket';
import { PaperAirplaneIcon, PhotoIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { supportDisplayRef } from '../lib/supportDisplayRef';

interface Msg {
  _id: string;
  sender: string;
  message: string;
  attachments: string[];
  createdAt: string;
  internalOnly?: boolean;
}

export default function SupportRequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const bottomRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [reply, setReply] = useState('');
  const [internalOnly, setInternalOnly] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingUrls, setPendingUrls] = useState<string[]>([]);
  const [detailsOpen, setDetailsOpen] = useState(true);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const res = await api.get(`/admin/support/requests/${id}`);
      const d = res.data.data;
      setData(d);
    } catch (e) {
      console.error(e);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!id) return;
    let sock: ReturnType<typeof getNotificationsSocket> | null = null;
    try {
      sock = getNotificationsSocket();
    } catch {
      return undefined;
    }
    const join = () => {
      sock?.emit('join_support_request', { requestId: id });
    };
    if (sock.connected) join();
    else sock.once('connect', join);
    const onThread = (payload: { requestId?: string }) => {
      if (payload?.requestId === id) load();
    };
    sock.on('support_request_thread', onThread);
    return () => {
      sock?.emit('leave_support_request', { requestId: id });
      sock?.off('support_request_thread', onThread);
    };
  }, [id, load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [data?.messages?.length]);

  const patchStatus = async (status: string) => {
    if (!id) return;
    try {
      await api.patch(`/admin/support/requests/${id}/status`, { status });
      await load();
    } catch (e) {
      console.error(e);
    }
  };

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      setUploading(true);
      const form = new FormData();
      form.append('file', file);
      form.append('folder', 'support');
      form.append('type', 'image');
      const res = await api.post('/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data?.data?.url;
      if (url) setPendingUrls((u) => [...u, url]);
    } catch (err) {
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const sendReply = async () => {
    if (!id) return;
    const text = reply.trim();
    if (!text && pendingUrls.length === 0) return;
    try {
      setSending(true);
      await api.post(`/admin/support/requests/${id}/message`, {
        message: text,
        attachments: pendingUrls,
        internalOnly,
      });
      setReply('');
      setPendingUrls([]);
      setInternalOnly(false);
      await load();
    } catch (e) {
      console.error(e);
    } finally {
      setSending(false);
    }
  };

  const canSend = reply.trim().length > 0 || pendingUrls.length > 0;

  const removePendingUrl = (url: string) => {
    setPendingUrls((prev) => prev.filter((u) => u !== url));
  };

  const onReplyKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      if (canSend && !sending) void sendReply();
    }
  };

  if (loading && !data) {
    return (
      <DashboardLayout title={t('supportRequests.detailTitle')}>
        <div className="text-center py-16 text-gray-600">{t('supportRequests.loading')}</div>
      </DashboardLayout>
    );
  }

  if (!data) {
    return (
      <DashboardLayout title={t('supportRequests.detailTitle')}>
        <p className="text-red-600">{t('supportRequests.notFound')}</p>
        <button type="button" className="mt-4 text-primary-orange underline" onClick={() => navigate('/')}>
          {t('supportRequests.backToList')}
        </button>
      </DashboardLayout>
    );
  }

  const user = data.user;
  const order = data.order;
  const orderMongoId = order
    ? String((order as { _id?: string })._id ?? (data as { orderId?: string }).orderId ?? '')
    : String((data as { orderId?: string }).orderId ?? '');
  const orderNumber = order && typeof order === 'object' ? (order as { orderNumber?: string }).orderNumber?.trim() : undefined;
  const orderRef = orderNumber || supportDisplayRef(orderMongoId);
  const requestRef = supportDisplayRef(data._id);

  const statusLabel = (s: string) => {
    if (s === 'in_progress') return t('supportRequests.statusInProgress');
    if (s === 'resolved') return t('supportRequests.statusResolved');
    return t('supportRequests.statusPending');
  };

  return (
    <DashboardLayout title={t('supportRequests.detailTitle')}>
      <div
        className={`flex flex-col gap-4 max-w-4xl mx-auto h-[calc(100dvh-5rem)] min-h-[760px] ${isRtl ? 'text-right' : ''}`}
        dir={isRtl ? 'rtl' : 'ltr'}
      >
        <button
          type="button"
          onClick={() => navigate('/?tab=supportRequests')}
          className="text-sm text-primary-orange hover:underline shrink-0"
        >
          ← {t('supportRequests.backToList')}
        </button>

        <div className="bg-white rounded-xl shadow-md overflow-hidden shrink-0 border border-gray-100">
          {/* Select must not live inside a <button> — that breaks collapse clicks in browsers. */}
          <div
            className={`flex flex-wrap items-start gap-3 p-4 sm:p-5 border-b border-gray-100 ${isRtl ? 'flex-row-reverse' : ''}`}
          >
            <button
              type="button"
              onClick={() => setDetailsOpen((v) => !v)}
              className={`flex flex-1 min-w-[min(100%,220px)] items-start gap-3 rounded-xl px-2 py-1 -m-1 hover:bg-gray-50/90 transition-colors ${isRtl ? 'text-right flex-row-reverse' : 'text-left'}`}
              aria-expanded={detailsOpen}
              aria-controls="support-request-details-panel"
              id="support-request-details-toggle"
            >
              <div className={`flex-1 min-w-0 ${isRtl ? 'text-right' : ''}`}>
                <p className="text-sm font-semibold text-[#E85D2A] tracking-wide mb-1">
                  {t('supportRequests.requestNumber', { ref: requestRef })}
                </p>
                <h1 className="text-lg sm:text-xl font-semibold text-gray-900 leading-snug">{data.subject}</h1>
                {!detailsOpen && (
                  <p className="text-sm text-gray-500 mt-1.5">
                    {orderMongoId
                      ? t('supportRequests.collapsedSummary', {
                          request: requestRef,
                          order: orderRef,
                          user: user?.name || t('supportRequests.userUnknown'),
                          status: statusLabel(data.status),
                        })
                      : t('supportRequests.collapsedSummaryNoOrder', {
                          request: requestRef,
                          user: user?.name || t('supportRequests.userUnknown'),
                          status: statusLabel(data.status),
                        })}
                  </p>
                )}
              </div>
              <ChevronDownIcon
                className={`w-6 h-6 text-gray-500 shrink-0 mt-0.5 transition-transform duration-200 ${detailsOpen ? 'rotate-180' : ''}`}
                aria-hidden
              />
            </button>
            <div className={`flex flex-col gap-1 shrink-0 ${isRtl ? 'items-end' : ''}`}>
              <label htmlFor="support-request-status" className="text-[10px] uppercase tracking-wide text-gray-400">
                {t('supportRequests.statusLabel')}
              </label>
              <select
                id="support-request-status"
                value={data.status}
                onChange={(e) => patchStatus(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm min-w-[148px] transition-shadow focus:ring-2 focus:ring-orange-200 bg-white"
              >
                <option value="pending">{t('supportRequests.statusPending')}</option>
                <option value="in_progress">{t('supportRequests.statusInProgress')}</option>
                <option value="resolved">{t('supportRequests.statusResolved')}</option>
              </select>
            </div>
          </div>

          {detailsOpen && (
            <div
              id="support-request-details-panel"
              role="region"
              aria-labelledby="support-request-details-toggle"
              className="px-4 sm:px-5 pb-5 pt-0"
            >
              <div className="grid md:grid-cols-2 gap-4 pt-4">
                <div className="rounded-lg bg-[#FFF8F2] p-4">
                  <h3 className="text-sm font-semibold text-gray-800 mb-2">{t('supportRequests.userInfo')}</h3>
                  <p className="text-sm text-gray-700">{user?.name || '—'}</p>
                  <p className="text-sm text-gray-600">{user?.email || '—'}</p>
                  {user?.phoneNumber && <p className="text-sm text-gray-600">{user.phoneNumber}</p>}
                </div>
                <div className="rounded-lg bg-[#FFF8F2] p-4">
                  <h3 className="text-sm font-semibold text-gray-800 mb-2">{t('supportRequests.orderSummary')}</h3>
                  {orderMongoId ? (
                    <>
                      <p className="text-base font-semibold text-gray-900 tracking-wide">
                        {t('supportRequests.orderNumber', { ref: orderRef })}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">
                        {t('supportRequests.orderTotal')}: {(order as { totalPriceEGP?: number })?.totalPriceEGP ?? '—'} EGP
                      </p>
                      <p className="text-sm text-gray-600">
                        {t('supportRequests.orderStatus')}: {(order as { status?: string })?.status ?? '—'}
                      </p>
                      <button
                        type="button"
                        onClick={() => navigate(`/orders/${orderMongoId}`)}
                        className="mt-2 text-sm text-[#E85D2A] font-medium hover:underline"
                      >
                        {t('supportRequests.openOrderDetails')}
                      </button>
                    </>
                  ) : (
                    <p className="text-sm text-gray-500">—</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-md border border-gray-100 flex flex-col flex-1 min-h-0 p-4 sm:p-5">
          <h2 className="text-lg font-semibold text-gray-800 mb-3 shrink-0">{t('supportRequests.conversation')}</h2>
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain space-y-3 mb-4 pr-1 -mr-1">
            {(data.messages as Msg[]).map((m, idx) => (
              <div
                key={`${m.createdAt}-${idx}-${m.sender}`}
                className={`flex ${m.sender === 'admin' ? (isRtl ? 'justify-start' : 'justify-end') : isRtl ? 'justify-end' : 'justify-start'}`}
                style={{ animation: 'fadeIn 0.25s ease-out' }}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2 shadow-sm ${
                    m.internalOnly
                      ? 'bg-amber-50 border border-amber-200 text-amber-950'
                      : m.sender === 'admin'
                        ? 'bg-[#E85D2A] text-white'
                        : 'bg-gray-100 text-gray-900'
                  }`}
                >
                  {m.internalOnly && (
                    <p className="text-xs font-semibold mb-1 opacity-80">{t('supportRequests.internalTag')}</p>
                  )}
                  {m.message && <p className="text-sm whitespace-pre-wrap">{m.message}</p>}
                  {m.attachments?.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {m.attachments.map((u) => (
                        <a
                          key={u}
                          href={u}
                          target="_blank"
                          rel="noreferrer"
                          className={`block text-xs underline ${m.sender === 'admin' && !m.internalOnly ? 'text-white/90' : 'text-blue-600'}`}
                        >
                          {t('supportRequests.attachmentLink')}
                        </a>
                      ))}
                    </div>
                  )}
                  <p className={`text-[10px] mt-1 opacity-70 ${isRtl ? 'text-left' : 'text-right'}`}>
                    {new Date(m.createdAt).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          <div className="shrink-0 space-y-3">
          {pendingUrls.length > 0 && (
            <div className={`flex flex-wrap gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
              <span className="text-xs font-medium text-gray-500 w-full sm:w-auto sm:self-center">
                {t('supportRequests.pendingAttachments')}:
              </span>
              {pendingUrls.map((url, imgIdx) => (
                <span
                  key={url}
                  className="inline-flex items-center gap-1.5 max-w-full pl-3 pr-1 py-1.5 rounded-full text-xs font-medium bg-orange-50 text-orange-900 border border-orange-200/80 shadow-sm"
                >
                  <span className="truncate max-w-[200px]">{t('supportRequests.pendingImageN', { n: imgIdx + 1 })}</span>
                  <button
                    type="button"
                    onClick={() => removePendingUrl(url)}
                    className="p-1 rounded-full text-orange-700 hover:bg-orange-100/80 transition-colors"
                    aria-label={t('supportRequests.removePendingAttachment')}
                  >
                    <XMarkIcon className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
            </div>
          )}

          <div
            className={`rounded-2xl border border-gray-200/90 bg-gradient-to-b from-[#FFFCFA] to-gray-50/80 shadow-sm ring-1 ring-black/[0.03] overflow-hidden ${isRtl ? 'text-right' : ''}`}
          >
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              onKeyDown={onReplyKeyDown}
              placeholder={t('supportRequests.replyPlaceholder')}
              rows={3}
              className={`w-full resize-y min-h-[5.5rem] max-h-48 border-0 bg-transparent px-4 py-3.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0 ${isRtl ? 'text-right' : 'text-left'}`}
            />
            <div
              className={`flex flex-wrap items-center justify-between gap-3 px-3 py-2.5 border-t border-gray-200/70 bg-white/60 backdrop-blur-[2px] ${isRtl ? 'flex-row-reverse' : ''}`}
            >
              <div className={`flex flex-wrap items-center gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
                <label
                  className={`inline-flex items-center justify-center gap-2 h-10 px-3.5 rounded-xl border text-sm font-medium cursor-pointer transition-all
                    ${uploading ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-wait' : 'border-gray-200 bg-white text-gray-700 hover:border-[#E85D2A]/40 hover:bg-orange-50/50 hover:text-[#E85D2A] shadow-sm'}
                    ${isRtl ? 'flex-row-reverse' : ''}`}
                >
                  <PhotoIcon className="w-5 h-5 shrink-0 opacity-90" />
                  <span className="hidden sm:inline">{uploading ? '…' : t('supportRequests.attach')}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={onFile} disabled={uploading} />
                </label>
                <button
                  type="button"
                  role="switch"
                  aria-checked={internalOnly}
                  onClick={() => setInternalOnly((v) => !v)}
                  className={`inline-flex items-center gap-2 h-10 px-3.5 rounded-xl text-sm font-medium border transition-all shadow-sm ${isRtl ? 'flex-row-reverse' : ''}
                    ${
                      internalOnly
                        ? 'border-amber-300 bg-amber-50 text-amber-950 ring-2 ring-amber-200/60'
                        : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:border-gray-300'
                    }`}
                >
                  <span
                    dir="ltr"
                    className={`relative inline-block h-5 w-9 shrink-0 rounded-full transition-colors ${internalOnly ? 'bg-amber-400' : 'bg-gray-300'}`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform duration-200 ease-out ${internalOnly ? 'translate-x-4' : 'translate-x-0'}`}
                    />
                  </span>
                  {t('supportRequests.internalOnly')}
                </button>
              </div>
              <button
                type="button"
                onClick={() => void sendReply()}
                disabled={sending || !canSend}
                className={`inline-flex items-center justify-center gap-2 min-h-10 px-5 rounded-xl text-sm font-semibold text-white shadow-md transition-all
                  bg-gradient-to-b from-[#F0703A] to-[#E85D2A] hover:from-[#E85D2A] hover:to-[#D64F20] hover:shadow-lg
                  disabled:opacity-45 disabled:cursor-not-allowed disabled:shadow-none disabled:hover:from-[#F0703A] disabled:hover:to-[#E85D2A]
                  ${isRtl ? 'flex-row-reverse' : ''}`}
              >
                {sending ? (
                  <span className="inline-block w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <PaperAirplaneIcon className="w-5 h-5 shrink-0" />
                )}
                {t('supportRequests.send')}
              </button>
            </div>
            <p className={`px-4 pb-2 text-[11px] text-gray-400 ${isRtl ? 'text-right' : 'text-left'}`}>
              {t('supportRequests.composerKeyboardHint')}
            </p>
          </div>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </DashboardLayout>
  );
}
